/*
Purpose: Context provider for app-wide notifications/toasts.
Audience: Entire app.
What it does:
- Exposes functions to show success/error/info messages
- Renders a container for transient notifications
Interplay:
- Can be used by analysis flows to surface errors (e.g., cost, provider issues)
*/
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { collection, onSnapshot, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';

const NotificationsContext = createContext({
  notify: (_msg) => {},
  hasUnseenAlerts: false
});

export const useNotifications = () => useContext(NotificationsContext);

const Toast = ({ toast, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, toast.timeoutMs || 9000);
    return () => clearTimeout(t);
  }, [onClose, toast.timeoutMs]);
  const color = toast.severity === 'error' ? '#991b1b' : toast.severity === 'warning' ? '#92400e' : '#065f46';
  const bg = toast.severity === 'error' ? '#fee2e2' : toast.severity === 'warning' ? '#fef3c7' : '#d1fae5';
  return (
    <div style={{
      background: bg,
      color,
      border: `1px solid ${color}33`,
      borderRadius: 10,
      padding: '10px 12px',
      boxShadow: '0 6px 18px rgba(0,0,0,0.15)',
      minWidth: 280,
      maxWidth: 420
    }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{toast.title}</div>
      <div style={{ fontSize: 13 }}>{toast.message}</div>
      {toast.actionLabel && (
        <button onClick={toast.onAction} style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color }}>
          {toast.actionLabel}
        </button>
      )}
    </div>
  );
};

export default function NotificationsProvider({ children }) {
  const { userId } = useAuth();
  const [toasts, setToasts] = useState([]);
  const [hasUnseenAlerts, setHasUnseenAlerts] = useState(false);
  const seenJobIdsRef = useRef(new Set());

  const notify = (t) => setToasts((prev) => [...prev, { id: Math.random().toString(36).slice(2), timeoutMs: 9000, ...t }]);
  const remove = (id) => setToasts((prev) => prev.filter(t => t.id !== id));

  // Listen for job cancellations/failures and completions (success)
  useEffect(() => {
    if (!userId) return;
    const jobsQ = query(
      collection(db, 'jobs'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const unsub = onSnapshot(jobsQ, (snap) => {
      snap.docChanges().forEach((change) => {
        if (change.type !== 'added' && change.type !== 'modified') return;
        const job = change.doc.data();
        const jobId = change.doc.id;
        // guard against duplicate toasts in-session
        const key = `${jobId}:${job.status}`;
        if (seenJobIdsRef.current.has(key)) return;

        if (job.status === 'cancelled' || job.status === 'failed') {
          seenJobIdsRef.current.add(key);
          setHasUnseenAlerts(true);
          notify({
            severity: 'error',
            title: job.status === 'cancelled' ? 'Analysis Cancelled' : 'Analysis Failed',
            message: job.errorMessage || 'The analysis could not be completed.',
            actionLabel: 'View Job',
            onAction: () => {
              // default action: open Firestore job doc in console
              window.open(`https://console.firebase.google.com/project/_/firestore/data/~2Fjobs~2F${jobId}`, '_blank');
            }
          });
        }
        if (job.status === 'completed') {
          seenJobIdsRef.current.add(key);
          notify({
            severity: 'success',
            title: 'Analysis Completed',
            message: `Evidence ${job.evidenceId} processed successfully.`,
            actionLabel: 'Open Results',
            onAction: () => {
              // simple navigation to dashboard; results modal will pick it up if wired
              window.location.hash = '#/dashboard';
            }
          });
        }
      });
    });
    return () => unsub();
  }, []);

  const ctxValue = useMemo(() => ({ notify, hasUnseenAlerts, clearAlerts: () => setHasUnseenAlerts(false) }), [hasUnseenAlerts]);

  return (
    <NotificationsContext.Provider value={ctxValue}>
      {children}
      {/* Toast container */}
      <div style={{ position: 'fixed', top: 14, right: 14, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {toasts.map(t => (
          <div key={t.id}>
            <Toast toast={t} onClose={() => remove(t.id)} />
          </div>
        ))}
      </div>
    </NotificationsContext.Provider>
  );
}

